import {createRouter, createWebHistory, type RouteRecordRaw} from 'vue-router';
import store from '@/store';
import {callAPI} from '@/store/utils';
import {isUserLogged} from '@/utils/user.js';
import routes from './index';
import routesAuth from './authRoutes';
import publicRoutes from './publicRoutes';
import publicAuthRoutes from './publicAuthRoutes';
import {setForceLight} from '@/composables/useTheme';

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const subdomain = hostname.split('.')[0] ?? '';

// Legacy Fygr production domain. While Fygr becomes Okimia, this host is frozen:
// every navigation is locked to the announcement page below. Only this exact
// hostname is affected — auth.app.fygr.io (subdomain "auth"), app.okimia.io,
// dev/test and local environments all keep working normally.
const FYGR_OKIMIA_ROUTE = 'fygr-becomes-okimia';
const isFygrLegacyHost = hostname === 'app.fygr.io' || hostname === 'dev.fygr.io' || hostname === 'test.fygr.io';

const appRoutes: RouteRecordRaw[] = subdomain === 'auth' ? routesAuth : routes;

// Zones toujours affichées en clair : sign-in et tout le tunnel d'onboarding.
// Le dark mode n'est accessible que depuis l'intérieur de l'app authentifiée,
// ce qui évite d'avoir à retravailler ces parcours pour le thème sombre.
const LIGHT_ONLY_PREFIXES = [
  'signin',
  'signup',
  'personal-',
  'company-infos',
  'partner-',
  'recovery',
  'completeSignUp',
  'presentation',
];
const shouldForceLight = (name?: string | symbol | null): boolean => {
  if (subdomain === 'auth') return true;
  if (typeof name !== 'string') return false;
  return LIGHT_ONLY_PREFIXES.some((p) => name === p || name.startsWith(p));
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: appRoutes,
});

const checkAccess = async (redirectTo: string) => {
  if (
    !store.state.user.permissions ||
    Object.keys(store.state.user.permissions).length === 0
  ) {
    await store.dispatch('user/actionGetPermissions');
  }
  if (
    !store.state.user.companyInfos ||
    Object.keys(store.state.user.companyInfos).length === 0
  ) {
    await store.dispatch('user/actionGetCompanyInfos');
  }

  return store.state.user.isCompanyCpa
    ? cpaRules(redirectTo)
    : nonCpaRules(redirectTo);
};

const cpaRules = (redirectTo: string) => {
  const perms = store.state.user.permissions;
  if (
    !perms ||
    Object.keys(perms).length === 0 ||
    Object.values(perms).every((value) => value === false)
  ) {
    return '/no-permissions';
  }
  const consoleAccess =
    perms.cpap_view_admin_console ||
    perms.cpap_access_all_managed_companies ||
    perms.cpap_can_see_aggregated_data;

  const cabinetAccess =
    perms.cpap_update_billing ||
    perms.cpap_update_payment_method ||
    perms.cpap_invoices_access ||
    perms.cpap_cpa_company_access ||
    perms.cpap_view_cpa_company_info ||
    perms.cpap_update_pricing;

  const usersAccess =
    perms.cpap_add_cpa_user ||
    perms.cpap_edit_cpa_permissions ||
    perms.cpap_add_cpa_staff_accountant ||
    perms.cpap_edit_cpa_staff_accountant_permissions ||
    perms.cpap_add_custom_permission_user ||
    perms.cpap_edit_custom_permission_groups ||
    perms.cpap_create_custom_permission_groups ||
    perms.cpap_transfer_property ||
    perms.cpap_can_invite_user;

  const redirectToAvailble = consoleAccess
    ? '/console'
    : cabinetAccess
      ? '/cabinet '
      : usersAccess
        ? '/users'
        : '/no-permissions';

  if (redirectTo === 'console' || redirectTo === '/console') {
    if (consoleAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'cabinet' || redirectTo === '/cabinet') {
    if (cabinetAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'users' || redirectTo === '/users') {
    if (usersAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'dashboard' || redirectTo === '/dashboard') {
    return redirectToAvailble;
  }
  return redirectTo;
};
const nonCpaRules = (redirectTo: string) => {
  const perms = store.state.user.permissions;
  if (
    !perms ||
    Object.keys(perms).length === 0 ||
    Object.values(perms).every((value) => value === false)
  ) {
    return '/no-permissions';
  }
  const dashboardAccess =
    perms.basic_info || perms.view_forecast || perms.edit_forecast;

  const transactionsAccess =
    perms.transactions_info ||
    perms.allow_classification ||
    perms.manage_all_payment_cards ||
    perms.manage_own_payment_cards;

  const checksAccess =
    perms.invoices_page_access && store.state.user.companyInfos.checks_allowed;

  const invoicesAccess =
    perms.invoices_page_access &&
    store.state.user.companyInfos.invoices_allowed;

  const redirectToAvailble = dashboardAccess
    ? '/dashboard'
    : transactionsAccess
      ? '/transactions'
      : invoicesAccess
        ? '/invoices'
        : checksAccess
          ? '/checks'
          : '/no-permissions';

  if (redirectTo === 'dashboard' || redirectTo === '/dashboard') {
    if (dashboardAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'checks' || redirectTo === '/checks') {
    if (checksAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'invoices' || redirectTo === '/invoices') {
    if (invoicesAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'transactions' || redirectTo === '/transactions') {
    if (transactionsAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  if (redirectTo === 'analytics' || redirectTo === '/analytics') {
    if (dashboardAccess) {
      return redirectTo;
    }
    return redirectToAvailble;
  }
  return redirectTo;
};

router.beforeEach(async (to, from, next) => {
  // Force le thème clair sur les pages d'auth/onboarding (voir shouldForceLight),
  // sans écraser la préférence de l'utilisateur pour l'intérieur de l'app.
  setForceLight(shouldForceLight(to.name));

  // Lock the legacy app.fygr.io domain to the Okimia announcement page only.
  // This runs before any auth/permission logic so the page stays fully public
  // and reachable, and no other page can ever be navigated to on this host.
  if (isFygrLegacyHost) {
    if (to.name === FYGR_OKIMIA_ROUTE) {
      return next();
    }
    return next({name: FYGR_OKIMIA_ROUTE});
  }

  if (
    !isUserLogged() ||
    from.name === 'signin' ||
    from.name === 'signin-callback' ||
    !from.name
  ) {
    try {
      const res = store.dispatch('user/actionGetUserPreferences');
      if (res.status_code === 401 || res.status_code === 422 || res.error) {
        store.dispatch('user/actionLogout', {}, {root: true});
      }
    } catch (e) {
      console.warn(e);
      store.dispatch('user/actionLogout', {}, {root: true});
    }
  }

  // auth or app
  if (subdomain === 'auth') {
    if (publicAuthRoutes.includes(to.name as string)) {
      next();
      return true;
    }
    next('/error');
    return true;
  }

  // redirect to signin page if not logged in and trying to access a restricted page
  const authRequired = !publicRoutes.includes(to.name as string);

  // if i am not logged in
  if (!isUserLogged()) {
    // if it needs user to be logged in
    if (authRequired) {
      store.dispatch('user/actionLogout', {}, {root: true});
    }
    // if it doesn't need authentification
    if (!authRequired) {
      return next();
    }
  }

  // Honour ?companyId=… in the URL: switch active company if needed and
  // continue to the clean target URL. Reload is required so every store
  // module re-fetches with the new company context.
  if (isUserLogged()) {
    const rawTarget = to.query.companyId;
    const targetCompanyId = Array.isArray(rawTarget) ? rawTarget[0] : rawTarget;
    const UUID_RE =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (typeof targetCompanyId === 'string' && UUID_RE.test(targetCompanyId)) {
      const currentCompanyId = localStorage.getItem('FYGR_COMPANY');
      const cleanQuery = {...to.query};
      delete cleanQuery.companyId;
      if (targetCompanyId !== currentCompanyId) {
        localStorage.setItem('FYGR_COMPANY', targetCompanyId);
        const resolved = router.resolve({
          path: to.path,
          query: cleanQuery,
          hash: to.hash,
        });
        window.location.replace(resolved.href);
        return;
      }
      return next({
        path: to.path,
        query: cleanQuery,
        hash: to.hash,
        replace: true,
      });
    }
  }

  // if is logged => check token validation before
  const companyId = localStorage.getItem('FYGR_COMPANY');
  if (
    (!companyId || companyId === 'undefined') &&
    !['signin', 'signin-callback'].includes(to.name as string)
  ) {
    store.dispatch('user/actionLogout', {}, {root: true});
  }

  if (isUserLogged() && companyId) {
    let res;
    try {
      res = await callAPI('POST', 'verifyTokenExpiration');
    } catch (e) {
      console.warn(e);
      store.dispatch('user/actionLogout', {}, {root: true});
    }
    if (res.status_code === 401 || res.status_code === 422 || res.error) {
      store.dispatch('user/actionLogout', {}, {root: true});
    }
    if (res?.result?.need_user_signin) {
      store.commit('user/setSigninPopup', true, {root: true});
    }
    if (res?.result?.signup_not_finished) {
      store.commit(
        'user/setFinishingSignup',
        res?.result?.signup_not_finished,
        {
          root: true,
        },
      );
    }
    if (!store.state.user?.profileData?.profile?.email) {
      await store.dispatch('user/actionGetUserProfile');
    }
    if (
      (!store.state?.user?.profileData ||
        (!store.state.user?.profileData?.profile?.firstName &&
          !store.state.user?.profileData?.profile?.lastName &&
          !store.state.user?.profileData?.profile?.telephone) ||
        (store.state.user?.profileData?.profile?.firstName?.trim().length ||
          0) < 1 ||
        (store.state.user?.profileData?.profile?.lastName?.trim().length || 0) <
          1) &&
      !store.state.user?.pretendedUser &&
      from.name !== 'personal-infos' &&
      from.name !== 'company-infos' &&
      to.name !== 'personal-infos'
    ) {
      return next('/personal-infos');
    }
  }

  document.title = 'Okimia';

  if (
    ['signup', 'signup-en', 'signin'].includes(from.name as string) &&
    !['signup', 'signup-en', 'signin'].includes(to.name as string)
  ) {
    store.commit('app/setSuppressOnboarding', false, {root: true});
  }

  // Here we get the first allowed page depending from "to" dynamically
  const allowedNext = await checkAccess(to.path);

  // if we would like to redirect to Dashboard as default start page - we check if it available. if not - get available page
  const allowedNextDashboard = await checkAccess('dashboard');

  // if user not allowed to access invoices or checks
  // if (['invoices', 'checks'].includes(to.name)) {
  //   const permRes = await callAPI('POST', 'getPermissions', {companyId});
  //   if (to.name === 'invoices' && !permRes.result.invoices_page_access) {
  //     return next('/dashboard');
  //   }
  //   const res = await callAPI('POST', 'getCompanyInfos', {companyId});
  //   if (res.status_code !== 401 || res.error !== true) {
  //     if (
  //       to.name === 'invoices' &&
  //       res &&
  //       res.result &&
  //       Object.prototype.hasOwnProperty.call(res.result, 'invoices_allowed') &&
  //       res.result.invoices_allowed === true
  //     ) {
  //       return next();
  //     }
  //     if (
  //       to.name === 'checks' &&
  //       res &&
  //       res.result &&
  //       res.result.invoices_allowed !== undefined &&
  //       res.result.checks_allowed === true
  //     ) {
  //       return next();
  //     }
  //   }
  //   return next('/dashboard');
  // }

  // ---------------START middleware to use for the pages -----------------------

  // Demo company: not provisioned with embedded banking / payment permissions,
  // but any user may browse the real e-banking module (data is faked inside the
  // components). Skip the company/permission gates for banking routes in that case.
  const routeName = String(to.name || '');
  const isBankingRoute =
    routeName === 'transactions' ||
    routeName.startsWith('e-banking') ||
    routeName.startsWith('banking-payments');
  const skipPermissionGatesForDemo =
    isBankingRoute && store.state.user?.selectedCompany?.is_demo === true;

  const requiredCompanyPermissions = to.meta.companyPermissions as
    | string[]
    | undefined;
  if (
    !skipPermissionGatesForDemo &&
    requiredCompanyPermissions &&
    Array.isArray(requiredCompanyPermissions)
  ) {
    // Ensure we have company info loaded in store (used for company-level feature flags)
    if (!store.state.user?.selectedCompany && companyId) {
      try {
        await store.dispatch('user/actionGetCompanyInfo', companyId, {
          root: true,
        });
      } catch (e) {
        console.warn(e);
      }
    }

    const company = store.state.user?.selectedCompany;
    const allowed = requiredCompanyPermissions.every(
      (p) => company?.[p] === true,
    );
    if (!allowed) {
      return next({name: 'no-permissions'});
    }
  }

  const requiredPermissions = to.meta.permissions as string[] | undefined;
  const userPermissions = store.state.user.permissions;
  if (
    !skipPermissionGatesForDemo &&
    requiredPermissions &&
    Array.isArray(requiredPermissions)
  ) {
    const allowed = requiredPermissions.some(
      (p) => userPermissions[p] === true,
    );

    if (!allowed) {
      return next({name: 'no-permissions'});
    }
  }

  // ---------------END middleware to use for the pages -----------------------

  if (to.name === 'libeo-integration') {
    const res = await callAPI('POST', 'getCompanyInfos', {companyId});
    if (res) {
      if (!res?.result?.is_cpa && isUserLogged()) {
        return next(allowedNext);
      }
      return next('/console');
    }
  }

  // if (['personal-infos', 'partner-selection', 'partner-integration'].includes(to.name)) {
  //   const res = await callAPI('POST', 'getCompanyInfos', { companyId });
  //   // res.partner_integration_visibility = true;
  //   // added setTimeout due to race conditions in store
  //   if (['partner-integration'].includes(to.name)) {
  //     if (from.name === 'Console') {
  //       store.commit('user/setFromLocation', from.name);
  //     } else {
  //       store.commit('user/setFromLocation', null);
  //     }
  //   }
  //   if (res) {
  //     store.commit('partner/setPartnerIntegrationVisibility',
  //     res?.partner_integration_visibility);
  //     if (!res?.is_cpa) {
  //       return next('/dashboard');
  //     }
  //     if (res?.is_cpa && !res.partner_integration_visibility) {
  //       return next('/console');
  //     }
  //     if (res?.is_cpa && isLogged) {
  //       return next();
  //     }
  //     return next();
  //   }
  // }

  // if i want to access public pages (except acceptInvite) but already logged in,
  // redirect to dashboard,
  if (
    publicRoutes.includes(to.name as string) &&
    ![
      'acceptInvite',
      'completeSignUp',
      'maintenance',
      'sconnect',
      'design-system',
      'fygr-becomes-okimia',
    ].includes(to.name as string) &&
    isUserLogged()
  ) {
    return next(
      store.state.user.isCompanyCpa ? '/console' : allowedNextDashboard,
    );
  }
  // Else

  if (
    allowedNext.replace(/[^a-zA-Z]/g, '') !== to.path.replace(/[^a-zA-Z]/g, '')
  ) {
    next(allowedNext);
  } else {
    next();
  }
  return true;
});
router.afterEach((to) => {
  // const toIndex = ['/signup', '/signup-en', '/signin'];
  // const isToIndex = toIndex.includes(to.path);
  // const isSubRoute = toIndex.some((route) => {
  //   return to.path.startsWith(`${route}/`);
  // });
  // const robots = isToIndex && !isSubRoute ? 'index,follow' : 'noindex,nofollow';

  // document.querySelector('meta[name="robots"]').setAttribute('content', robots);

  let canonical = '';
  if (to.path.includes('/signup') && to.name !== 'signup') {
    canonical = `${window.location.origin}/signup`;
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute('href', canonical);
  }
  const pageName =
    typeof to.name === 'string'
      ? `Page ${to.name} viewed`
      : to.name != null
        ? `Page ${String(to.name)} viewed`
        : `Page ${to.path} viewed`;
  if (typeof window !== 'undefined') {
    window.analytics?.page(pageName);
  }
});

export default router;
